import { useEffect, useState, useCallback } from "react";
import { Globe, Plus, Loader2, MapPin, Users2, Check, X, UserRound, Car } from "lucide-react";
import { adminApi, formatApiError } from "./api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";

// Canadian provinces & territories for the "add" picker
const ALL_PROVINCES = [
  "Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador",
  "Nova Scotia", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan",
  "Northwest Territories", "Nunavut", "Yukon",
];

export function GeographicPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/provinces").then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, active: 0, inactive: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (p) => {
    setBusy(p.name);
    try {
      await adminApi.patch(`/provinces/${encodeURIComponent(p.name)}`, { active: !p.active });
      setData((d) => ({
        ...d,
        items: d.items.map((x) => (x.name === p.name ? { ...x, active: !x.active } : x)),
        active: d.active + (p.active ? -1 : 1),
        inactive: d.inactive + (p.active ? 1 : -1),
      }));
      toast.success(`${p.name} ${p.active ? "disabled" : "enabled"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update province");
    } finally {
      setBusy("");
    }
  };

  const existing = new Set((data?.items || []).map((p) => p.name));
  const available = ALL_PROVINCES.filter((p) => !existing.has(p));

  const addProvince = async () => {
    if (!newName) return toast.error("Please choose a province");
    setAdding(true);
    try {
      await adminApi.post("/provinces", { name: newName });
      toast.success(`${newName} added`);
      setAddOpen(false); setNewName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not add province");
    } finally {
      setAdding(false);
    }
  };

  const stat = (label, value, color) => (
    <div className="rounded-2xl border border-[#12142b]/8 bg-white p-5 shadow-sm">
      <p className="text-sm font-medium text-[#565b73]">{label}</p>
      <p className="mt-2 font-display text-3xl font-black" style={{ color }}>{value}</p>
    </div>
  );

  return (
    <div data-testid="geographic-control-panel" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-[#12142b]">Geographic Control</h2>
          <p className="text-sm text-[#565b73]">Provinces where SafeRide is currently available.</p>
        </div>
        <button data-testid="add-province-btn" onClick={() => setAddOpen(true)} className="flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390]">
          <Plus className="h-4 w-4" /> Add Province
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {stat("Provinces Onboarded", data ? data.total : "—", "#12142b")}
          {stat("Live / Available", data ? data.active : "—", "#16a34a")}
          {stat("Disabled", data ? data.inactive : "—", "#565b73")}
        </div>

        <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-[#12142b]"><Globe className="h-5 w-5 text-[#2340b5]" /> Coverage</h2>
          {!data && <p className="py-10 text-center text-sm text-[#565b73]">Loading provinces…</p>}
          {data && data.items.length === 0 && <p className="py-10 text-center text-sm text-[#565b73]">No provinces configured yet. Add one to start.</p>}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="provinces-grid">
            {data?.items.map((p) => (
              <div key={p.name} data-testid={`province-${p.name.replace(/\s+/g, "-").toLowerCase()}`} className={`flex items-center justify-between gap-3 rounded-xl border p-4 transition-colors ${p.active ? "border-[#16a34a]/25 bg-[#16a34a]/5" : "border-[#12142b]/10 bg-[#f9fafd]"}`}>
                <div className="flex items-center gap-3">
                  <span className={`grid h-10 w-10 place-items-center rounded-xl ${p.active ? "bg-[#16a34a]/12" : "bg-[#12142b]/6"}`}>
                    <MapPin className={`h-5 w-5 ${p.active ? "text-[#16a34a]" : "text-[#565b73]"}`} />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-[#12142b]">{p.name}</p>
                    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#565b73]">
                      <span className="inline-flex items-center gap-1"><Users2 className="h-3 w-3" /> {p.group_count} communit{p.group_count === 1 ? "y" : "ies"}</span>
                      <span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3" /> {p.members.toLocaleString()} member{p.members === 1 ? "" : "s"}</span>
                      <span className="inline-flex items-center gap-1"><Car className="h-3 w-3" /> {p.trips.toLocaleString()} trip{p.trips === 1 ? "" : "s"}</span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${p.active ? "bg-[#16a34a]/12 text-[#16a34a]" : "bg-[#565b73]/12 text-[#565b73]"}`}>
                        {p.active ? <><Check className="h-2.5 w-2.5" /> Available</> : <><X className="h-2.5 w-2.5" /> Disabled</>}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {busy === p.name && <Loader2 className="h-4 w-4 animate-spin text-[#565b73]" />}
                  <Switch data-testid={`province-toggle-${p.name.replace(/\s+/g, "-").toLowerCase()}`} checked={p.active} disabled={busy === p.name} onCheckedChange={() => toggle(p)} />
                </div>
              </div>
            ))}
          </div>
        </div>

      <Dialog open={addOpen} onOpenChange={(o) => !o && setAddOpen(false)}>
        <DialogContent className="max-w-md" data-testid="add-province-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-[#2340b5]" /> Add Province</DialogTitle>
            <p className="text-sm text-[#565b73]">Expand SafeRide coverage to a new province or territory.</p>
          </DialogHeader>
          {available.length === 0 ? (
            <p className="py-6 text-center text-sm text-[#565b73]">All provinces and territories are already onboarded.</p>
          ) : (
            <div className="grid max-h-[45vh] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
              {available.map((name) => (
                <button
                  key={name} data-testid={`add-option-${name.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setNewName(name)}
                  className={`rounded-xl border px-3 py-2.5 text-left text-sm font-semibold transition-colors ${newName === name ? "border-[#2340b5] bg-[#2340b5]/8 text-[#2340b5]" : "border-[#12142b]/12 text-[#12142b] hover:bg-[#f4f6fb]"}`}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setAddOpen(false)} className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">Cancel</button>
            <button data-testid="add-province-confirm" disabled={adding || !newName || available.length === 0} onClick={addProvince} className="flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390] disabled:opacity-50">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add {newName}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
